import { HeroUIProvider } from "@heroui/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";

import App from "./App";
import "./i18n";
import "./index.css";
import {TraceInformation} from "./components/layout/tracing";

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

const intervalMS = 60 * 1000

// // add this to prompt for a refresh
const updateSW = registerSW({
  onNeedRefresh() {
    if (confirm("A new version of the app is available. Update?")) {
      updateSW(true);
    }
  },
  onRegisteredSW(_swUrl, registration) {
    if (registration) {
      const safeUpdate = async () => {
        // Only try updates when page is visible and online
        if (document.visibilityState !== "visible") return;
        if (!navigator.onLine) return;

        // Ensure the registration is ready and active
        try {
          await navigator.serviceWorker.ready;
        } catch {
          // If ready fails, skip this tick
          return;
        }

        // Skip if no active worker (installing/waiting/redundant can throw)
        if (!registration.active) return;

        try {
          registration.update();
        } catch (err: any) {
          if (err?.name === "InvalidStateError") {
            // Benign transient error; will retry on next tick or when visible/online
            return;
          }
          // eslint-disable-next-line no-console
          console.error("ServiceWorker registration.update() failed:", err);
        }
      };

      // Periodic checks only while the page is visible
      let intervalId: number | undefined;
      const startInterval = () => {
        if (intervalId != null) return;
        intervalId = window.setInterval(safeUpdate, intervalMS);
      };
      const stopInterval = () => {
        if (intervalId != null) {
          clearInterval(intervalId);
          intervalId = undefined;
        }
      };

      // Manage interval based on visibility
      const handleVisibility = () => {
        if (document.visibilityState === "visible") {
          startInterval();
          // Try an immediate check when becoming visible
          void safeUpdate();
        } else {
          stopInterval();
        }
      };

      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("online", () => void safeUpdate());

      // Kick things off with current state
      handleVisibility();

      // Clean up on unload
      window.addEventListener("beforeunload", () => {
        stopInterval();
        document.removeEventListener("visibilitychange", handleVisibility);
      });
    }
  },
});

root.render(
  <React.StrictMode>
    <HeroUIProvider>
      <React.Suspense fallback="loading...">
        <App />
      </React.Suspense>
    </HeroUIProvider>
  </React.StrictMode>
);
